// Pure utility functions for the web-beacon tracking script.
// Extracted for testability; esbuild bundles them into the final script.

export function generateUUID() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
}

export function readCookies() {
  const cookies = {};
  document.cookie.split(";").forEach(pair => {
    const [rawKey, ...rawVal] = pair.split("=");
    const key = decodeURIComponent((rawKey || "").trim());
    const val = decodeURIComponent(rawVal.join("="));
    if (key) cookies[key] = val;
  });
  return cookies;
}

export function writeCookie(name, value, daysValid = 365, domain = null) {
  const expires = new Date(Date.now() + daysValid * 864e5).toUTCString();
  const domainAttr = domain ? `Domain=${domain}; ` : "";
  document.cookie =
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ` +
    `${domainAttr}Path=/; expires=${expires}; Secure; SameSite=Lax`;
}

export function refreshSessionCookie(sessionId, sessionCookieName, sessionDurationMinutes, domain = null) {
  const expires = new Date(Date.now() + sessionDurationMinutes * 60 * 1000).toUTCString();
  const domainAttr = domain ? `Domain=${domain}; ` : "";
  document.cookie =
    `${sessionCookieName}=${encodeURIComponent(sessionId)}; ` +
    `${domainAttr}Path=/; expires=${expires}; Secure; SameSite=Lax`;
}

// Curated list of multi-segment public suffixes covering the markets we serve.
// Not exhaustive — the full Public Suffix List is ~14k entries. These cover
// North America, Europe, Australia/NZ, and major Asia/LATAM economies.
// If a hostname's last two labels aren't in here, we assume a flat TLD
// (.com, .io, .ca, .de, etc.) and the two-label fallback applies.
const TWO_PART_TLDS = new Set([
  // United Kingdom
  "co.uk", "org.uk", "net.uk", "me.uk", "ltd.uk", "plc.uk", "ac.uk", "sch.uk", "gov.uk", "nhs.uk", "police.uk",
  // Australia
  "com.au", "net.au", "org.au", "edu.au", "gov.au", "asn.au", "id.au",
  // New Zealand
  "co.nz", "net.nz", "org.nz", "ac.nz", "govt.nz", "school.nz",
  // Canada (provincial + federal; most .ca use flat, but provincial exists)
  "ab.ca", "bc.ca", "mb.ca", "nb.ca", "nf.ca", "nl.ca", "ns.ca", "nt.ca", "nu.ca",
  "on.ca", "pe.ca", "qc.ca", "sk.ca", "yk.ca", "gc.ca",
  // Mexico
  "com.mx", "org.mx", "net.mx", "edu.mx", "gob.mx",
  // Brazil
  "com.br", "net.br", "org.br", "gov.br", "edu.br", "mil.br", "art.br", "blog.br",
  // Argentina
  "com.ar", "net.ar", "org.ar", "gov.ar", "edu.ar",
  // Europe (multi-segment markets only — .de, .fr, .it, .nl, .se, etc. are flat)
  "com.es", "org.es", "net.es", "edu.es", "nom.es", "gob.es",
  "co.at", "or.at", "ac.at", "gv.at",
  "com.pt", "org.pt", "net.pt", "edu.pt", "gov.pt",
  "com.gr", "net.gr", "org.gr", "edu.gr", "gov.gr",
  "com.pl", "net.pl", "org.pl", "info.pl", "edu.pl", "gov.pl",
  // Japan
  "co.jp", "ne.jp", "or.jp", "ac.jp", "ad.jp", "ed.jp", "go.jp", "lg.jp", "gr.jp",
  // South Korea
  "co.kr", "ne.kr", "or.kr", "re.kr", "pe.kr", "go.kr", "ac.kr", "hs.kr", "ms.kr", "es.kr",
  // India
  "co.in", "net.in", "org.in", "gov.in", "ac.in", "edu.in", "gen.in", "firm.in", "ind.in",
  // China
  "com.cn", "net.cn", "org.cn", "gov.cn", "edu.cn", "ac.cn", "mil.cn",
  // Hong Kong
  "com.hk", "net.hk", "org.hk", "gov.hk", "edu.hk", "idv.hk",
  // Taiwan
  "com.tw", "net.tw", "org.tw", "gov.tw", "edu.tw", "idv.tw",
  // Singapore
  "com.sg", "net.sg", "org.sg", "gov.sg", "edu.sg",
  // Malaysia
  "com.my", "net.my", "org.my", "gov.my", "edu.my", "mil.my",
  // Indonesia
  "co.id", "net.id", "or.id", "go.id", "ac.id", "sch.id", "mil.id", "web.id",
  // Thailand
  "co.th", "net.th", "or.th", "go.th", "ac.th", "in.th",
  // Israel
  "co.il", "net.il", "org.il", "gov.il", "ac.il", "k12.il", "muni.il",
  // Turkey
  "com.tr", "net.tr", "org.tr", "gov.tr", "edu.tr", "mil.tr", "k12.tr", "web.tr",
  // South Africa
  "co.za", "net.za", "org.za", "gov.za", "ac.za", "edu.za",
]);

// Resolve a hostname to its registrable domain, returning a leading-dot form
// suitable for a cookie Domain attribute (".acme.com"). Returns null for
// hosts where Domain= is not allowed (IPs, localhost, single-label hosts) —
// callers should omit the Domain attribute and let the browser use host-only
// scope.
export function registrableDomain(hostname) {
  if (!hostname || typeof hostname !== "string") return null;

  const host = hostname.toLowerCase().replace(/\.$/, "");

  // Reject IPv4, IPv6, and any bracketed host form
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  if (host.startsWith("[") || host.includes(":")) return null;

  const parts = host.split(".");
  if (parts.length < 2) return null; // localhost, intranet names, etc.

  const lastTwo = parts.slice(-2).join(".");
  if (TWO_PART_TLDS.has(lastTwo)) {
    if (parts.length < 3) return null; // bare public suffix, nothing registrable
    return "." + parts.slice(-3).join(".");
  }

  return "." + lastTwo;
}

// Resolve the beacon endpoint by inspecting the script tag that loaded us.
// Order: data-endpoint override (HTTPS only) → script origin + /v2/e → fallback.
// The fallback keeps direct-embed installs (who never went first-party) working.
//
// data-endpoint is an operator-facing override (WordPress plugin env switcher,
// local dev pointing at staging, etc.). We require https: so a compromised
// script tag can't silently downgrade beacons to http:// or redirect them via
// javascript:/data: URLs. If override is malformed or non-HTTPS, we ignore it
// and fall through to the normal origin-derived endpoint.
export function resolveEndpoint(script, fallback) {
  if (script) {
    const override = script.getAttribute && script.getAttribute("data-endpoint");
    if (override) {
      try {
        if (new URL(override).protocol === "https:") return override;
      } catch (_) { /* malformed override — fall through to origin inference */ }
    }

    try {
      const origin = new URL(script.src).origin;
      if (origin && origin !== "null") return `${origin}/v2/e`;
    } catch (_) { /* unparseable src — fall through */ }
  }

  return fallback;
}

// True if `origin` (an event.origin string like "https://widget.example.com")
// belongs to `domain` — either an exact apex match or a subdomain. Parses as
// URL so we can't be fooled by substring matches (e.g. origin.includes("foo")
// matches "foo.com" but also "evilfoo.com" and "foo.com.evil.com").
export function isOriginUnder(origin, domain) {
  try {
    const host = new URL(origin).hostname.toLowerCase();
    const d = domain.toLowerCase();
    return host === d || host.endsWith("." + d);
  } catch (_) {
    return false;
  }
}

// Luhn mod-10 checksum. Every real credit card number passes Luhn; only ~10%
// of random same-length digit strings do. Gated on digit-only length 12-19
// (PCI-DSS PAN spec) so non-numeric inputs short-circuit before the loop.
export function looksLikeCreditCard(digits) {
  if (!/^\d{12,19}$/.test(digits)) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = +digits[i];
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

// Returns "[redacted]" for values that look like SSNs or credit card numbers,
// otherwise the original value. Anchored regexes only match when the entire
// value is the sensitive payload — "call 555-12-3456 for support" is not
// treated as an SSN. CC matching strips spaces/dashes before the digit check
// and requires Luhn to pass, so "1234567890123456" (not a real card) is kept.
export function redactSensitive(value) {
  if (!value || typeof value !== "string") return value;
  const trimmed = value.trim();
  // SSN: strictly 3-2-4 digits with dashes. Bare 9-digit strings are too
  // ambiguous (could be account numbers, phone digits, etc.) so we only
  // redact when the classic SSN formatting is present. This also keeps
  // zip+4 ("94103-1234") and similar 5-4 patterns safe.
  if (/^\d{3}-\d{2}-\d{4}$/.test(trimmed)) return "[redacted]";
  // Credit card: strip common separators, then length-gate + Luhn.
  if (looksLikeCreditCard(trimmed.replace(/[\s-]/g, ""))) return "[redacted]";
  return value;
}
